import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { daznService, WvdStatus, getErrorMessage } from '@/services';

const MAX_BYTES = 64 * 1024;

/** The user's own Widevine device file: upload, replace, delete. Nobody else's file is reachable from here. */
export default function WvdCard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WvdStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    daznService.getWvd().then(setStatus).catch((error) => toast.error(getErrorMessage(error) || t('common.error')));
  }, [t]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.wvd') || file.size === 0 || file.size > MAX_BYTES) {
      toast.error(t('wvd.badFile'));
      return;
    }
    setBusy(true);
    try {
      setStatus(await daznService.uploadWvd(file));
      toast.success(t('wvd.uploaded'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await daznService.deleteWvd();
      setStatus({ uploaded: false });
      setConfirmDelete(false);
      toast.success(t('wvd.deleted'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const rejected = status.uploaded && status.lastStatus === 'rejected';
  const accepted = status.uploaded && status.lastStatus === 'ok';
  const shared = status.uploaded && status.sharedSystemId;
  const badge = !status.uploaded
    ? { cls: 'badge-error', label: t('wvd.badgeMissing') }
    : rejected
      ? { cls: 'badge-error', label: t('wvd.badgeRejected') }
      : shared
        ? { cls: 'badge-warning', label: t('wvd.badgeWarning') }
        : { cls: 'badge-success', label: t('wvd.badgeActive') };

  return (
    <div className="card space-y-4">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-dark-100">{t('wvd.title')}</h2>
          <p className="text-dark-400 text-sm mt-1 max-w-2xl">{t('wvd.intro')}</p>
        </div>
        <span className={badge.cls}>{badge.label}</span>
      </div>

      {status.uploaded && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            [t('wvd.file'), status.originalName],
            [t('wvd.systemId'), status.systemId ?? '—'],
            [t('wvd.level'), status.securityLevel ? `L${status.securityLevel}` : '—'],
            [t('wvd.uploadedOn'), dayjs(status.uploadedAt).format('DD/MM/YYYY HH:mm')],
          ].map(([label, value]) => (
            <div key={String(label)} className="bg-dark-800 rounded-lg px-3 py-2 min-w-0">
              <dt className="text-dark-500 text-xs uppercase tracking-wider">{label}</dt>
              <dd className="text-dark-200 text-sm font-medium break-all">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {!status.uploaded && (
        <div className="bg-warning-50 border border-warning-100 rounded-lg p-3 text-sm text-warning-600">{t('wvd.missingNote')}</div>
      )}
      {shared && !rejected && (
        <div className="bg-warning-50 border border-warning-100 rounded-lg p-3 text-sm text-warning-600">{t('wvd.sharedNote')}</div>
      )}
      {rejected && status.uploaded && (
        <div className="bg-error-50 border border-error-100 rounded-lg p-3 text-sm text-error-600">
          {t('wvd.rejectedNote', { code: status.lastStatusCode ?? '' })}
        </div>
      )}
      {accepted && status.uploaded && (
        <p className="text-success-600 text-sm">
          {t('wvd.acceptedNote', { date: dayjs(status.lastStatusAt).format('DD/MM/YYYY HH:mm') })}
        </p>
      )}

      <div
        className={`border-2 border-dashed rounded-lg p-5 text-center space-y-2 transition-colors ${
          dragOver ? 'border-primary-500 bg-primary-50' : 'border-dark-700'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); void upload(e.dataTransfer.files[0]); }}
      >
        <p className="text-dark-300 text-sm font-medium">{status.uploaded ? t('wvd.dropReplace') : t('wvd.dropTitle')}</p>
        <input ref={fileInput} type="file" accept=".wvd" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
        <button type="button" className="btn-primary" disabled={busy} onClick={() => fileInput.current?.click()}>
          {status.uploaded ? t('wvd.replaceFile') : t('wvd.chooseFile')}
        </button>
        <p className="text-dark-500 text-xs">{t('wvd.hint')}</p>
      </div>

      {status.uploaded && !confirmDelete && (
        <button type="button" className="btn-secondary text-error-600" disabled={busy} onClick={() => setConfirmDelete(true)}>
          {t('wvd.delete')}
        </button>
      )}
      {status.uploaded && confirmDelete && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-dark-300">
          <span>{t('wvd.deleteConfirm')}</span>
          <button type="button" className="btn-secondary text-error-600" disabled={busy} onClick={() => void remove()}>{t('wvd.deleteYes')}</button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
        </div>
      )}

      <div className="border-t border-dark-700 pt-3">
        <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{t('wvd.rulesTitle')}</p>
        <ul className="list-disc pl-5 text-dark-400 text-xs space-y-0.5">
          <li>{t('wvd.rule1')}</li>
          <li>{t('wvd.rule2')}</li>
          <li>{t('wvd.rule3')}</li>
        </ul>
      </div>
    </div>
  );
}
